import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../../agent-config/agent-config.service';
import {RenameAgentThreadRequest,RenameAgentThreadResponse} from '../../../models';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';

/**
 * Rename Agent Thread Service
 *
 * Handles renaming a single conversation thread for agents.
 *
 * Features:
 * - Rename a thread by identifiers
 * - Mock mode for development/testing
 * - Configurable via JSON files
 * - Error handling and logging
 */
@Injectable({
  providedIn: 'root',
})
export class RenameAgentThreadService {
  private useMock: boolean = true;
  private baseUrl: string = '/api/agent';
  private endpointPath: string = '/thread';
  private mockResponse: RenameAgentThreadResponse | null = null;

  constructor(
    private logger: LoggerService,
    private agentConfigService: AgentConfigService,
    private agentApiService: AgentApiService
  ) {
    // Load configuration values (AgentConfigService already loaded at app startup)

    this.loadConfigurationValues();
  }

  /**
   * Load configuration values from AgentConfigService
   * Note: AgentConfigService is already loaded during app initialization,
   * so we just extract the values we need. URL resolution is now handled by AgentApiService.
   */
  private loadConfigurationValues(): void {
    try {
      // 1. Get API config for mock mode
      const apiConfig = this.agentConfigService.getApiConfig() || {};
      this.useMock = apiConfig?.useMock ?? true;
      // 2. Keep fallback values for backward compatibility
      this.baseUrl = apiConfig?.baseUrl || '/api/agent';
      this.endpointPath = '/thread';
      // 3. Get mock responses if available
      if (this.useMock) {
        const mockConfig = this.agentConfigService.getMockResponses();
        this.mockResponse = mockConfig?.responses?.update_agent_thread || null;
      }
      // 4. Get the actual endpoint URL from AgentApiService configuration

      const configuredEndpoint =
        this.agentApiService.getApiUrlFromConfig('update_agent_thread');

      this.logger.logInfo(
        `[GetAgentThreads] Configuration loaded - useMock: ${
          this.useMock
        }, endpoint: ${
          configuredEndpoint || `${this.baseUrl}${this.endpointPath}`
        }`,
        'loadConfigurationValues'
      );
    } catch (error: any) {
      this.logger.logInfo(
        '[RenameAgentThread] Error loading configuration values, using defaults',
        'loadConfigurationValues',
        error
      );
    }
  }

  /**
   * Rename Agent Thread
   *
   * Renames a conversation thread using the provided identifiers and new name.
   *
   * @param request - RenameAgentThreadRequest containing required identifiers and new name
   * @returns Observable<RenameAgentThreadResponse>
   */
  RenameAgentThread(
    request: RenameAgentThreadRequest,
    agentId?: string
  ): Observable<RenameAgentThreadResponse> {
    try {
      // Validate request
      const validationError = this.validateRequest(request);
      if (validationError) {
        return of(validationError);
      }

      // Check if mock mode is enabled
      if (this.useMock && this.mockResponse) {
        this.logger.logInfo(
          '[RenameAgentThread] Using mock response',
          'RenameAgentThread'
        );
        return of(this.mockResponse);
      }

      // Make actual API call
      this.logger.logInfo(
        '[RenameAgentThread] Making API call',
        'RenameAgentThread'
      );
      return this.makeApiCall(request, agentId);
    } catch (error: any) {
      this.logger.logInfo(
        '[RenameAgentThread] Error in RenameAgentThread',
        'RenameAgentThread',
        error
      );
      return of(
        this.createErrorResponse('Internal error occurred', 'INTERNAL_ERROR')
      );
    }
  }

  /**
   * Perform API call to rename the agent thread via AgentApiService.
   *
   * @param request - RenameAgentThreadRequest
   * @param agentId - Agent identifier
   * @returns Observable<RenameAgentThreadResponse>
   */
  private makeApiCall(
    request: RenameAgentThreadRequest,
    agentId?: string
  ): Observable<RenameAgentThreadResponse> {
    try {
      const endpoint =
        this.agentApiService.getApiUrlFromConfig('update_agent_thread', agentId) ||
        `${this.baseUrl}${this.endpointPath}`;

      const requestBody: any = {
        tenant_id: request.tenant_id,
        user_id: request.user_id,
        thread_id: request.thread_id,
        thread_name: request.thread_name
      };

      const customBaseUrl = this.agentApiService.getBaseUrlForEndpoint('update_agent_thread', agentId);

      const supervisor = this.agentApiService.getSupervisorForAgent(agentId);
      if (supervisor && supervisor.length > 0) {
        requestBody.is_deleted = false;
      }

      this.logger.logInfo(
        `[RenameAgentThread] ${agentId || 'default'} – calling ${endpoint}`,
        'makeApiCall'
      );

      return this.agentApiService
        .put<any>(endpoint, requestBody, undefined, customBaseUrl)
        .pipe(
          map((response: any) => {
            const result: RenameAgentThreadResponse = this.isCentralizedResponse(response)
              ? {
                  status: 'success',
                  message: response.message || 'Thread renamed successfully',
                  data: {
                    thread_id: response.data?.thread_id || request.thread_id,
                    thread_name: response.data?.thread_name || request.thread_name,
                    user_id: request.user_id,
                    tenant_id: request.tenant_id,
                    updated_at: response.data?.agent_thread_updated_at || new Date().toISOString()
                  }
                }
              : response;

            this.logger.logInfo(
              `[RenameAgentThread] API call successful - status: ${result.status}`,
              'makeApiCall'
            );
            return result;
          }),
          catchError((error: any) => {
            this.logger.logInfo(
              '[RenameAgentThread] API call failed',
              'makeApiCall',
              error
            );
            return of(this.handleApiError(error));
          })
        );
    } catch (error: any) {
      this.logger.logInfo(
        '[RenameAgentThread] Error making API call',
        'makeApiCall',
        error
      );
      return of(
        this.createErrorResponse('Failed to make API call', 'API_ERROR')
      );
    }
  }

  private isCentralizedResponse(response: any): boolean {
    return response?.status?.toLowerCase() === 'success' && response?.data && typeof response.data === 'object';
  }

  /**
   * Validate rename request payload
   *
   * @param request - RenameAgentThreadRequest
   * @returns RenameAgentThreadResponse | null (null if valid)
   */
  private validateRequest(
    request: RenameAgentThreadRequest
  ): RenameAgentThreadResponse | null {
    try {
      // Check required fields
      if (!request.tenant_id || request.tenant_id.trim() === '') {
        return this.createErrorResponse(
          'tenant_id is required',
          'VALIDATION_ERROR',
          {
            tenant_id: 'Field is required',
          }
        );
      }

      if (!request.user_id || request.user_id.trim() === '') {
        return this.createErrorResponse(
          'user_id is required',
          'VALIDATION_ERROR',
          {
            user_id: 'Field is required',
          }
        );
      }

      if (!request.thread_id || request.thread_id.trim() === '') {
        return this.createErrorResponse(
          'thread_id is required',
          'VALIDATION_ERROR',
          {
            thread_id: 'Field is required',
          }
        );
      }

      if (!request.thread_name || request.thread_name.trim() === '') {
        return this.createErrorResponse(
          'thread_name is required',
          'VALIDATION_ERROR',
          {
            thread_name: 'Field is required',
          }
        );
      }

      return null; // Validation passed
    } catch (error: any) {
      this.logger.logInfo(
        '[RenameAgentThread] Error validating request',
        'validateRequest',
        error
      );
      return this.createErrorResponse('Validation error', 'VALIDATION_ERROR');
    }
  }

  /**
   * Handle API errors
   *
   * @param error - HTTP error
   * @returns RenameAgentThreadResponse
   */
  private handleApiError(error: any): RenameAgentThreadResponse {
    try {
      if (error.status === 400) {
        return this.createErrorResponse(
          error.error?.message || 'Invalid request parameters',
          'VALIDATION_ERROR',
          error.error?.details
        );
      }

      if (error.status === 401) {
        return this.createErrorResponse(
          'Authentication required',
          'UNAUTHORIZED'
        );
      }

      if (error.status === 403) {
        return this.createErrorResponse(
          'Access denied for tenant',
          'FORBIDDEN'
        );
      }

      if (error.status === 404) {
        return this.createErrorResponse('Endpoint not found', 'NOT_FOUND');
      }

      if (error.status === 500) {
        return this.createErrorResponse(
          'Internal server error',
          'INTERNAL_ERROR'
        );
      }

      return this.createErrorResponse(
        error.error?.message || 'An unexpected error occurred',
        error.error?.code || 'UNKNOWN_ERROR'
      );
    } catch (e: any) {
      this.logger.logInfo(
        '[RenameAgentThread] Error handling API error',
        'handleApiError',
        e
      );
      return this.createErrorResponse(
        'Failed to handle error',
        'ERROR_HANDLING_ERROR'
      );
    }
  }

  /**
   * Create error response
   *
   * @param message - Error message
   * @param code - Error code
   * @param details - Optional error details
   * @returns RenameAgentThreadResponse
   */
  private createErrorResponse(
    message: string,
    code: string,
    details?: Record<string, string>
  ): RenameAgentThreadResponse {
    const response: RenameAgentThreadResponse = {
      status: 'error',
      message,
      code,
    };

    if (details) {
      response.details = details;
    }

    return response;
  }

  /**
   * Check if service is configured to use mock data
   *
   * @returns boolean
   */
  public isUsingMock(): boolean {
    return this.useMock;
  }

  /**
   * Get current API endpoint
   *
   * @returns string
   */
  public getEndpoint(): string {
    return (
      this.agentApiService.getApiUrlFromConfig('update_agent_thread') ||
      `${this.baseUrl}${this.endpointPath}`
    );
  }
}
